import { useCallback, useMemo, useState } from 'react';

import { type DateRange as CalendarRange } from 'react-day-picker';

import { type DateRange } from '@/modules/analytics/dateSelection';
import { toLocalDate } from '@/modules/analytics/utils';
import { formatDate } from '@/util/dateUtils';

/**
 * Holds a half-picked range until both ends exist, then commits and drops it.
 *
 * @param onCommitted Runs after a range is applied, for layouts that close themselves.
 */
export const useDateRangeDraft = (
  startDate: string,
  endDate: string,
  onRangeSelect: (range: DateRange) => void,
  onCommitted?: () => void
) => {
  const [draft, setDraft] = useState<CalendarRange | undefined>(undefined);

  const applied = useMemo<CalendarRange | undefined>(() => {
    const from = toLocalDate(startDate);
    if (!from) {
      return undefined;
    }
    return { from, to: toLocalDate(endDate) ?? undefined };
  }, [startDate, endDate]);

  const handleSelect = useCallback(
    (range: CalendarRange | undefined) => {
      if (range?.from && range?.to) {
        setDraft(undefined);
        onRangeSelect({ startDate: formatDate(range.from), endDate: formatDate(range.to) });
        onCommitted?.();
        return;
      }
      setDraft(range);
    },
    [onCommitted, onRangeSelect]
  );

  const clearDraft = useCallback(() => setDraft(undefined), []);

  return { shown: draft ?? applied, isDrafting: !!draft, handleSelect, clearDraft };
};
